import { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { createPortal } from 'react-dom';

interface FormikDevToolsProps {
	position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

let devToolsCount = 0;

export function FormikDevTools({ position = 'bottom-right' }: FormikDevToolsProps) {
	const [isOpen, setIsOpen] = useState(false);
	// eslint-disable-next-line react/hook-use-state
	const [instanceIndex] = useState(() => devToolsCount++);
	const formik = useFormikContext();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		ref.current?.removeAttribute('inert');
	}, []);

	const toggle = useCallback(() => setIsOpen(prev => !prev), []);

	if (process.env.NODE_ENV === 'production') {
		return null;
	}

	const positionClasses = {
		'bottom-left': 'bottom-4 left-4',
		'bottom-right': 'bottom-4 right-4',
		'top-left': 'top-4 left-4',
		'top-right': 'top-4 right-4'
	};

	const offset = instanceIndex * 40; // 60px spacing between buttons
	const bottom = instanceIndex === 1 ? 30 : offset;
	const offsetStyle = position.includes('bottom') ? { bottom: `${bottom}px` } : { top: `${16 + offset}px` };

	return createPortal(
		<div
			ref={ref}
			data-react-aria-top-layer
			className="fixed z-[9999]"
			style={{
				...offsetStyle,
				[position.includes('left') ? 'left' : 'right']: '16px'
			}}
		>
			<button
				onClick={toggle}
				type="button"
				className="flex size-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-colors hover:bg-blue-700"
				title="Formik DevTools"
			>
				📝
			</button>

			{/* Drawer */}
			{isOpen && (
				<div className="absolute bottom-16 right-0 size-96 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl">
					<div className="flex items-center justify-between border-b bg-gray-100 px-4 py-2">
						<h3 className="font-semibold text-gray-800">Formik DevTools</h3>
						<button type="button" onClick={toggle} className="text-gray-500 hover:text-gray-700">
							✕
						</button>
					</div>

					<div className="h-full overflow-auto p-4 text-sm">
						<div className="space-y-4">
							{/* Values */}
							<div>
								<h4 className="mb-2 font-medium text-gray-700">Values</h4>
								<pre className="overflow-auto rounded bg-gray-50 p-2 text-xs">{JSON.stringify(formik.values, null, 2)}</pre>
							</div>

							{/* Errors */}
							{Object.keys(formik.errors).length > 0 && (
								<div>
									<h4 className="mb-2 font-medium text-red-600">Errors</h4>
									<pre className="overflow-auto rounded bg-red-50 p-2 text-xs">{JSON.stringify(formik.errors, null, 2)}</pre>
								</div>
							)}

							{/* Touched */}
							{Object.keys(formik.touched).length > 0 && (
								<div>
									<h4 className="mb-2 font-medium text-yellow-600">Touched</h4>
									<pre className="overflow-auto rounded bg-yellow-50 p-2 text-xs">{JSON.stringify(formik.touched, null, 2)}</pre>
								</div>
							)}

							{/* Status */}
							<div>
								<h4 className="mb-2 font-medium text-gray-700">Status</h4>
								<div className="space-y-1 rounded bg-gray-50 p-2 text-xs">
									<div>isSubmitting: {formik.isSubmitting.toString()}</div>
									<div>isValidating: {formik.isValidating.toString()}</div>
									<div>isValid: {formik.isValid.toString()}</div>
									<div>dirty: {formik.dirty.toString()}</div>
									<div>submitCount: {formik.submitCount}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>,
		document.body
	);
}
